import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { pmsFrom, invokeFunction } from '@/integrations/pms';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { sanitizeHtml } from '@/lib/sanitize';

interface DynamicFormProps {
  formSlug?: string;
  formId?: string;
  pageSlug?: string;
  className?: string;
  /** When true, skips the is_active check — used for admin preview/test */
  skipActiveCheck?: boolean;
  /** Called after a successful submission */
  onSubmitSuccess?: () => void;
}

export const DynamicForm: React.FC<DynamicFormProps> = ({ formSlug, formId, pageSlug, className, skipActiveCheck, onSubmitSuccess }) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [consentEmail, setConsentEmail] = useState(false);
  const [consentSms, setConsentSms] = useState(false);
  const [acceptTermsGlobal, setAcceptTermsGlobal] = useState(false);
  const [acceptanceChecks, setAcceptanceChecks] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  // Fetch form by slug, id, or page assignment
  const { data: form, isLoading: formLoading } = useQuery({
    queryKey: ['dynamic-form', formSlug, formId, pageSlug],
    queryFn: async () => {
      let query = pmsFrom('custom_forms').select('*');
      if (!skipActiveCheck) {
        query = query.eq('is_active', true);
      }

      if (formId) {
        query = query.eq('id', formId);
      } else if (formSlug) {
        query = query.eq('slug', formSlug);
      } else if (pageSlug) {
        query = query.contains('assigned_pages', JSON.stringify([pageSlug]));
      }

      const { data, error } = await query.maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!(formSlug || formId || pageSlug),
  });

  // Fetch form fields
  const { data: fields = [], isLoading: fieldsLoading } = useQuery({
    queryKey: ['dynamic-form-fields', form?.id],
    queryFn: async () => {
      if (!form?.id) return [];
      const { data, error } = await pmsFrom('custom_form_fields')
        .select('*')
        .eq('form_id', form.id)
        .order('sort_order');
      if (error) throw error;
      return data;
    },
    enabled: !!form?.id,
  });

  const isTestMode = pageSlug === 'admin-test';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    for (const field of fields) {
      if (field.is_required) {
        if (field.field_type === 'acceptance') {
          if (!acceptanceChecks[field.id]) {
            toast({ title: `Please accept: ${field.label}`, variant: 'destructive' });
            return;
          }
        } else if (field.field_type === 'multiselect') {
          const val = formData[field.label];
          if (!Array.isArray(val) || val.length === 0) {
            toast({ title: `${field.label} is required`, variant: 'destructive' });
            return;
          }
        } else if (field.field_type === 'checkbox') {
          if (!formData[field.label]) {
            toast({ title: `${field.label} is required`, variant: 'destructive' });
            return;
          }
        } else if (!formData[field.label]?.toString().trim()) {
          toast({ title: `${field.label} is required`, variant: 'destructive' });
          return;
        }
      }
    }

    // Check form-specific terms
    if (form?.terms_required && !termsAccepted) {
      toast({ title: 'Please accept the terms and conditions', variant: 'destructive' });
      return;
    }

    // Validate global compliance checkboxes (skip in test mode)
    if (!isTestMode) {
      if (!acceptTermsGlobal) {
        toast({ title: 'Please accept the Terms of Service and Privacy Policy', variant: 'destructive' });
        return;
      }
      if (!consentEmail) {
        toast({ title: 'Please consent to email communications', variant: 'destructive' });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const submissionPayload: Record<string, any> = { ...formData };
      if (isTestMode) {
        submissionPayload._is_test = true;
      } else {
        submissionPayload._accept_terms = acceptTermsGlobal;
        submissionPayload._consent_email = consentEmail;
        submissionPayload._consent_sms = consentSms;
      }

      // Submit form data
      const { data: submissionData, error } = await pmsFrom('custom_form_submissions').insert({
        form_id: form!.id,
        data: submissionPayload,
        terms_accepted: form?.terms_required ? termsAccepted : null,
        page_submitted_from: isTestMode ? 'admin-test' : (pageSlug || window.location.pathname),
      }).select('id').single();

      if (error) throw error;

      // Create a lead entry (skip for test submissions to avoid polluting pipeline)
      if (!isTestMode) {
        try {
          const fd = formData as Record<string, any>;
          const findField = (...keys: string[]) => {
            for (const k of keys) {
              const match = Object.entries(fd).find(([key]) => key.toLowerCase().includes(k));
              if (match?.[1]) return String(match[1]);
            }
            return null;
          };
          const leadName = findField('name', 'full_name', 'first_name') || form?.name + ' Submission';
          const leadEmail = findField('email', 'e-mail');
          const leadPhone = findField('phone', 'tel', 'mobile', 'cell');
          const leadMessage = findField('message', 'comment', 'note', 'description', 'details');
          const leadService = findField('service', 'interest', 'topic', 'subject');

          await pmsFrom('leads').insert({
            name: leadName,
            email: leadEmail,
            phone: leadPhone,
            message: leadMessage,
            service_needed: leadService,
            source: `form:${form?.slug || form?.name || 'custom'}`,
            customer_type: 'new',
          });
        } catch (leadErr) {
          console.error('Failed to create lead from form submission:', leadErr);
        }
      }

      // Send notification if email is configured (skip for test submissions)
      if (form?.notification_email && submissionData?.id && !isTestMode) {
        try {
          await invokeFunction('notify-form-submission', {
            body: {
              submission_id: submissionData.id,
              form_name: form.name,
              notification_email: form.notification_email,
              notification_email_cc: form.notification_email_cc,
              data: formData,
              terms_accepted: termsAccepted,
              page: pageSlug || window.location.pathname,
            },
          });
        } catch (notifyError) {
          console.error('Failed to send notification:', notifyError);
        }
      }

      setIsSubmitted(true);
      toast({ title: isTestMode ? 'Test submission recorded!' : (form?.success_message || 'Form submitted successfully!') });
      onSubmitSuccess?.();
    } catch (error: any) {
      toast({ title: 'Error submitting form', description: error.message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (formLoading || fieldsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!form || fields.length === 0) return null;

  if (isSubmitted) {
    return (
      <div className={cn('text-center py-12', className)}>
        <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-primary" />
        <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
        <p className="text-muted-foreground">{form.success_message}</p>
        <Button variant="outline" className="mt-4" onClick={() => {
          setIsSubmitted(false);
          setFormData({});
          setTermsAccepted(false);
          setAcceptanceChecks({});
        }}>
          Submit Another Response
        </Button>
      </div>
    );
  }

  // Group fields into rows for half-width layout
  const renderFields = () => {
    const rows: JSX.Element[] = [];
    let i = 0;
    while (i < fields.length) {
      const field = fields[i];
      const nextField = fields[i + 1];

      if (field.width === 'half' && nextField?.width === 'half') {
        rows.push(
          <div key={i} className="grid grid-cols-2 gap-4">
            {renderField(field)}
            {renderField(nextField)}
          </div>
        );
        i += 2;
      } else {
        rows.push(<div key={i}>{renderField(field)}</div>);
        i += 1;
      }
    }
    return rows;
  };

  const renderField = (field: any) => {
    const value = formData[field.label] || '';
    const onChange = (val: string) => setFormData(prev => ({ ...prev, [field.label]: val }));

    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
      case 'url':
      case 'date':
        return (
          <div className="space-y-2" key={field.id}>
            <Label>{field.label} {field.is_required && <span className="text-destructive">*</span>}</Label>
            <Input
              type={field.field_type === 'phone' ? 'tel' : field.field_type}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={field.placeholder || ''}
              required={field.is_required}
            />
          </div>
        );

      case 'textarea':
        return (
          <div className="space-y-2" key={field.id}>
            <Label>{field.label} {field.is_required && <span className="text-destructive">*</span>}</Label>
            <Textarea
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={field.placeholder || ''}
              required={field.is_required}
              rows={4}
            />
          </div>
        );

      case 'select': {
        const selectOpts = (Array.isArray(field.options) ? field.options : []).filter((o: string) => o.trim());
        return (
          <div className="space-y-2" key={field.id}>
            <Label>{field.label} {field.is_required && <span className="text-destructive">*</span>}</Label>
            <Select value={value} onValueChange={onChange}>
              <SelectTrigger className="bg-background">
                <SelectValue placeholder={field.placeholder || 'Select...'} />
              </SelectTrigger>
              <SelectContent className="bg-popover border shadow-lg z-50">
                {selectOpts.map((opt: string, idx: number) => (
                  <SelectItem key={idx} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      }

      case 'multiselect': {
        const multiselectOpts = (Array.isArray(field.options) ? field.options : []).filter((o: string) => o.trim());
        const selectedValues: string[] = Array.isArray(formData[field.label]) ? formData[field.label] : [];
        const toggleOption = (opt: string) => {
          const updated = selectedValues.includes(opt)
            ? selectedValues.filter(v => v !== opt)
            : [...selectedValues, opt];
          setFormData(prev => ({ ...prev, [field.label]: updated }));
        };
        return (
          <div className="space-y-2" key={field.id}>
            <Label>{field.label} {field.is_required && <span className="text-destructive">*</span>}</Label>
            <div className="border rounded-md p-3 space-y-1 max-h-48 overflow-y-auto bg-background">
              {multiselectOpts.length === 0 ? (
                <p className="text-xs text-muted-foreground py-1">No options configured</p>
              ) : (
                multiselectOpts.map((opt: string, idx: number) => (
                  <label key={idx} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 px-2 py-1.5 rounded">
                    <Checkbox
                      checked={selectedValues.includes(opt)}
                      onCheckedChange={() => toggleOption(opt)}
                    />
                    <span>{opt}</span>
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedValues.length > 0
                ? `${selectedValues.length} of ${multiselectOpts.length} selected`
                : `Select one or more options${field.is_required ? '' : ' (optional)'}`}
            </p>
          </div>
        );
      }

      case 'radio': {
        const radioOpts = (Array.isArray(field.options) ? field.options : []).filter((o: string) => o.trim());
        return (
          <div className="space-y-2" key={field.id}>
            <Label>{field.label} {field.is_required && <span className="text-destructive">*</span>}</Label>
            <RadioGroup value={value} onValueChange={onChange}>
              {radioOpts.map((opt: string, idx: number) => (
                <div key={idx} className="flex items-center space-x-2">
                  <RadioGroupItem value={opt} id={`${field.id}-${idx}`} />
                  <Label htmlFor={`${field.id}-${idx}`}>{opt}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );
      }

      case 'checkbox':
        return (
          <div className="flex items-center space-x-2" key={field.id}>
            <Checkbox
              checked={!!formData[field.label]}
              onCheckedChange={(v) => setFormData(prev => ({ ...prev, [field.label]: !!v }))}
            />
            <Label>{field.label} {field.is_required && <span className="text-destructive">*</span>}</Label>
          </div>
        );

      case 'acceptance':
        return (
          <div className="flex items-start space-x-2" key={field.id}>
            <Checkbox
              checked={!!acceptanceChecks[field.id]}
              onCheckedChange={(v) => setAcceptanceChecks(prev => ({ ...prev, [field.id]: !!v }))}
              className="mt-1"
            />
            <div className="text-sm">
              {field.terms_text ? (
                <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(field.terms_text) }} className="[&_a]:text-primary [&_a]:underline" />
              ) : (
                <span>{field.label}</span>
              )}
              {field.is_required && <span className="text-destructive ml-1">*</span>}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className={cn('space-y-4', className)} data-form-name={form.name}>
      {form.description && (
        <p className="text-sm text-muted-foreground mb-4">{form.description}</p>
      )}

      {renderFields()}

      {/* Form-specific Terms & Conditions */}
      {form.terms_required && form.terms_content && (
        <div className="space-y-3 border rounded-lg p-4 bg-muted/20">
          <div
            className="text-sm prose prose-sm dark:prose-invert max-w-none max-h-40 overflow-y-auto"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(form.terms_content) }}
          />
          <div className="flex items-center space-x-2">
            <Checkbox
              checked={termsAccepted}
              onCheckedChange={(v) => setTermsAccepted(!!v)}
            />
            <Label className="text-sm">I agree to the terms and conditions <span className="text-destructive">*</span></Label>
          </div>
        </div>
      )}

      {/* TCPA / A2P Compliance Consent Section — hidden in test mode */}
      {!isTestMode && (
        <div className="space-y-3 border-t border-border pt-4">
          {/* Terms & Privacy Policy */}
          <div className="flex items-start gap-2">
            <Checkbox
              id="df-accept-terms"
              checked={acceptTermsGlobal}
              onCheckedChange={(v) => setAcceptTermsGlobal(!!v)}
              className="mt-0.5"
            />
            <Label htmlFor="df-accept-terms" className="text-xs text-background/70 leading-relaxed cursor-pointer">
              I agree to the{' '}
              <Link to="/terms-of-service" className="underline hover:text-primary" target="_blank">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy-policy" className="underline hover:text-primary" target="_blank">Privacy Policy</Link>. *
            </Label>
          </div>

          {/* Email Consent */}
          <div className="flex items-start gap-2">
            <Checkbox
              id="df-consent-email"
              checked={consentEmail}
              onCheckedChange={(v) => setConsentEmail(!!v)}
              className="mt-0.5"
            />
            <Label htmlFor="df-consent-email" className="text-xs text-background/70 leading-relaxed cursor-pointer">
              I consent to receive email communications regarding my inquiry. *
            </Label>
          </div>

          {/* Explicit SMS Opt-In */}
          <div className="flex items-start gap-2">
            <Checkbox
              id="df-consent-sms"
              checked={consentSms}
              onCheckedChange={(v) => setConsentSms(!!v)}
              className="mt-0.5"
            />
            <Label htmlFor="df-consent-sms" className="text-xs text-background/70 leading-relaxed cursor-pointer">
              I consent to receive SMS/text messages at the phone number provided. Message frequency varies. Message & data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase.
            </Label>
          </div>

          {/* SMS/Messaging Disclosure */}
          <p className="text-[10px] text-background/50 ml-6 leading-relaxed">
            By providing your phone number and checking the SMS box above, you authorize us to send text messages for service updates, appointment reminders, and promotional offers. 
            SMS data is collected, stored, and used in accordance with our{' '}
            <Link to="/privacy-policy" className="underline hover:text-primary" target="_blank">Privacy Policy</Link>. 
            We do not share your phone number or SMS consent data with third parties for marketing purposes.
          </p>
        </div>
      )}

      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
        {form.submit_button_text || 'Submit'}
      </Button>
    </form>
  );
};
